import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useEffect, useRef, useState } from "react";
import {
    ActivityIndicator,
    Platform,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    View
} from "react-native";
import { Text } from "react-native-paper";

import { DocumentEvidenceText } from "./DocumentEvidenceText";
import { useDocumentViewer, type DocumentRequest } from "./VisitDocumentViewer";

import {
    fetchContextSourceEvidence,
    type ContextItemView,
    type ContextSourceEvidence
} from "@/lib/api";
import { prettifyEvidencePlaceholder } from "@/lib/context-evidence-format";
import { colors, fonts, radius, spacing } from "@/lib/theme";

interface Props {
    visitId: string | number;
    items: ContextItemView[] | null;
    title: string | null;
    questionId?: number;
    onClose: () => void;
}

/** Show original source evidence, scoped to the hint and its question branch. */
export function ContextItemSheet({ visitId, items, title, questionId, onClose }: Props) {
    const openDocument = useDocumentViewer();
    const pendingDocument = useRef<DocumentRequest | null>(null);
    const showPendingDocument = () => {
        if (pendingDocument.current) {
            const request = pendingDocument.current;
            pendingDocument.current = null;
            openDocument(request);
        }
    };
    useEffect(() => {
        if (!items?.length && Platform.OS !== "ios") {
            showPendingDocument();
        }
    }, [items]);
    const [evidence, setEvidence] = useState<ContextSourceEvidence[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        setEvidence(null);
        setError(null);
        if (items?.length) {
            const unique = [...new Map(items.map((item) => [item.sourceId, item])).values()];
            Promise.all(
                unique.map((item) => fetchContextSourceEvidence(visitId, item.id, questionId))
            )
                .then((result) => {
                    if (!cancelled) {
                        setEvidence(result);
                    }
                })
                .catch((e) => {
                    if (!cancelled) {
                        setError(e instanceof Error ? e.message : "Could not load the source.");
                    }
                });
        }
        return () => {
            cancelled = true;
        };
    }, [visitId, items, questionId]);
    return (
        <Modal
            visible={!!items?.length}
            transparent
            animationType="slide"
            onRequestClose={onClose}
            onDismiss={showPendingDocument}
        >
            <Pressable style={styles.backdrop} onPress={onClose} />
            <View style={styles.sheet}>
                <View style={styles.handle} />
                <View style={styles.headerRow}>
                    <Text style={styles.title}>{title ?? "Source"}</Text>
                    <Pressable onPress={onClose} hitSlop={12} accessibilityLabel="Close">
                        <MaterialCommunityIcons
                            name="close"
                            size={20}
                            color={colors.textSecondary}
                        />
                    </Pressable>
                </View>
                <ScrollView contentContainerStyle={styles.body}>
                    {error ? (
                        <Text style={styles.imageFailed}>{error}</Text>
                    ) : !evidence ? (
                        <ActivityIndicator color={colors.teal500} />
                    ) : null}
                    {evidence?.map((source, index) => (
                        <View key={index} style={styles.item}>
                            {source.kind === "text" ? (
                                <>
                                    <Text style={styles.meta}>
                                        {questionId == null
                                            ? "Document text"
                                            : "Supporting document text"}
                                    </Text>
                                    <DocumentEvidenceText text={source.text ?? ""} />
                                </>
                            ) : source.kind === "document" && source.url ? (
                                <>
                                    <Text style={styles.meta}>Original document</Text>
                                    <Pressable
                                        accessibilityRole="button"
                                        onPress={() => {
                                            const url = source.url;
                                            if (url) {
                                                pendingDocument.current = {
                                                    title: source.title ?? "Document",
                                                    cacheKey:
                                                        source.cacheKey ??
                                                        `source:${visitId}:${items?.[index]?.sourceId}`,
                                                    contentType:
                                                        source.contentType ?? "application/pdf",
                                                    loadUrl: async () => url
                                                };
                                                onClose();
                                            }
                                        }}
                                    >
                                        <Text
                                            style={[
                                                styles.content,
                                                {
                                                    color: colors.info600,
                                                    textDecorationLine: "underline"
                                                }
                                            ]}
                                        >
                                            Open document
                                        </Text>
                                    </Pressable>
                                </>
                            ) : (
                                <>
                                    <Text style={styles.meta}>Recorded questions and answers</Text>
                                    {source.questions.length === 0 ? (
                                        <Text style={styles.content}>
                                            This hint does not have a linked recorded question. Its
                                            evidence may come from a narrative, medication or
                                            diagnosis entry.
                                        </Text>
                                    ) : null}
                                    {source.questions.map((q) => (
                                        <View
                                            key={q.id}
                                            style={[styles.item, { paddingVertical: spacing.sm }]}
                                        >
                                            <Text
                                                style={[
                                                    styles.content,
                                                    { fontFamily: fonts.semiBold }
                                                ]}
                                            >
                                                {q.question}
                                            </Text>
                                            {q.options.length
                                                ? q.options.map((o) => (
                                                      <Text key={o.value} style={styles.content}>
                                                          <Text
                                                              style={{
                                                                  color:
                                                                      o.state === "selected"
                                                                          ? colors.success600
                                                                          : o.state ===
                                                                              "not_selected"
                                                                            ? colors.error600
                                                                            : colors.textSecondary
                                                              }}
                                                          >
                                                              {o.state === "selected"
                                                                  ? "✓"
                                                                  : o.state === "not_selected"
                                                                    ? "✗"
                                                                    : "—"}
                                                          </Text>{" "}
                                                          {o.text}
                                                      </Text>
                                                  ))
                                                : null}
                                            <Text style={styles.content}>
                                                Answer:{" "}
                                                {q.options.some((o) => o.state === "selected")
                                                    ? q.options
                                                          .filter((o) => o.state === "selected")
                                                          .map((o) => o.text)
                                                          .join(", ")
                                                    : q.answer === "[]"
                                                      ? "No options selected"
                                                      : prettifyEvidencePlaceholder(q.answer)}
                                            </Text>
                                            {q.parentIds.length ? (
                                                <Text style={styles.meta}>Follow-up question</Text>
                                            ) : null}
                                        </View>
                                    ))}
                                </>
                            )}
                        </View>
                    ))}
                </ScrollView>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    // The scrim over the visit behind the sheet: the theme's ink at 35%. A
    // sibling of the sheet, not its parent, so the opacity dims only the
    // backdrop. (theme.ts has no scrim token yet; ink is the design's dark.)
    backdrop: {
        flex: 1,
        backgroundColor: colors.ink,
        opacity: 0.35
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius.xl,
        borderTopRightRadius: radius.xl,
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xl,
        maxHeight: "75%"
    },
    handle: {
        alignSelf: "center",
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.gray300,
        marginTop: spacing.sm,
        marginBottom: spacing.md
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: spacing.sm
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.ink,
        flex: 1
    },
    body: {
        gap: spacing.md,
        paddingBottom: spacing.md
    },
    item: {
        gap: spacing.xs
    },
    meta: {
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.textTertiary,
        letterSpacing: 0.3,
        textTransform: "uppercase"
    },
    content: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 21,
        color: colors.textPrimary
    },
    image: {
        width: "100%",
        aspectRatio: 4 / 3,
        borderRadius: radius.md,
        backgroundColor: colors.gray100,
        marginTop: spacing.xs
    },
    imageLoading: {
        marginVertical: spacing.md
    },
    imageFailed: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textTertiary,
        fontStyle: "italic"
    }
});
